import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";

import { resetTouchInput, setTouchKey, type TouchKey } from "./input";

interface TouchButtonProps {
  label: string;
  ariaLabel: string;
  variant?: "default" | "jump" | "shoot";
  onPress: (pressed: boolean) => void;
}

function TouchButton({ label, ariaLabel, variant = "default", onPress }: TouchButtonProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const pressedRef = useRef(false);

  const setPressed = useCallback(
    (next: boolean) => {
      if (pressedRef.current === next) return;
      pressedRef.current = next;
      onPress(next);
      ref.current?.classList.toggle("active", next);
    },
    [onPress],
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    ref.current?.setPointerCapture(event.pointerId);
    setPressed(true);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (ref.current?.hasPointerCapture(event.pointerId)) {
      ref.current.releasePointerCapture(event.pointerId);
    }
    setPressed(false);
  };

  // Falls der Pointer das Element verlässt (Daumen rutscht weg), Taste loslassen
  const handlePointerLeave = () => setPressed(false);
  const handlePointerCancel = () => setPressed(false);

  // ContextMenü und Tap-Highlight verhindern
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const block = (e: Event) => e.preventDefault();
    el.addEventListener("contextmenu", block);
    return () => el.removeEventListener("contextmenu", block);
  }, []);

  return (
    <button
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      className={`touch-btn ${variant !== "default" ? variant : ""}`}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerLeave={handlePointerLeave}
    >
      {label}
    </button>
  );
}

interface TouchControlsProps {
  /**
   * `overlay`: drei Buttons in den unteren Ecken (z. B. wenn das Spiel den
   * ganzen Bildschirm füllt). `bar`: drei Buttons als breite Reihe unter
   * dem Spielbereich – ideal für Hochkant-Modus auf dem Smartphone.
   */
  layout?: "overlay" | "bar";
}

export function TouchControls({ layout = "overlay" }: TouchControlsProps) {
  const set = useCallback((key: TouchKey) => (pressed: boolean) => setTouchKey(key, pressed), []);

  useEffect(() => {
    return () => resetTouchInput();
  }, []);

  if (layout === "bar") {
    return (
      <div className="touch-controls touch-controls-bar" aria-hidden="false">
        <TouchButton label="◀" ariaLabel="Nach links bewegen" onPress={set("left")} />
        <TouchButton label="↑" ariaLabel="Springen" variant="jump" onPress={set("jump")} />
        <TouchButton label="▶" ariaLabel="Nach rechts bewegen" onPress={set("right")} />
        <TouchButton label="✦" ariaLabel="Schießen" variant="shoot" onPress={set("shoot")} />
      </div>
    );
  }

  return (
    <div className="touch-controls touch-controls-overlay" aria-hidden="false">
      <div className="touch-pad">
        <TouchButton label="◀" ariaLabel="Nach links bewegen" onPress={set("left")} />
        <TouchButton label="▶" ariaLabel="Nach rechts bewegen" onPress={set("right")} />
      </div>
      <div className="touch-pad">
        <TouchButton label="✦" ariaLabel="Schießen" variant="shoot" onPress={set("shoot")} />
        <TouchButton
          label="↑"
          ariaLabel="Springen"
          variant="jump"
          onPress={set("jump")}
        />
      </div>
    </div>
  );
}
